import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { store } from 'stores/Store';
import styles from './SendTab.module.scss';

export interface SendTabProps {
  isOpen: boolean;
  isLoading?: boolean;
  error?: string;
  draftResult?: { subject: string, body: string };
  setIsOpen: (isOpen: boolean) => void;
  onSelect: (
    opt: 'email-self' | 'email-draft' | 'phone' | 'website',
    bulletPoints?: string,
    useRecentCheckingNotes?: boolean,
    useAccountNotes?: boolean,
  ) => void;
  cancelDraftRequest: () => void;
  openDraftLink: () => void;
}

const SendTab = ({isOpen, setIsOpen, onSelect, isLoading = false, error, draftResult, cancelDraftRequest, openDraftLink}: SendTabProps) => {
  const [sendStep, setSendStep] = useState<'select-send-method' | 'select-email-draft' | 'email-draft-inputs'>('select-send-method');

  const [bulletPoints, setBulletPoints] = useState('');
  const [useRecentCheckingNotes, setUseRecentCheckingNotes] = useState(false);
  const [useAccountNotes, setUseAccountNotes] = useState(false);

  // Check if user has early release access for AI email drafting
  const hasAIEmailAccess = store.user?.earlyRelease ?? false;

  const handleEmailClick = () => {
    // If user has early release access, show AI draft options
    // Otherwise, go directly to email-self (standard mailto)
    if (hasAIEmailAccess) {
      setSendStep('select-email-draft');
    } else {
      onSelect('email-self');
    }
  };

  const handleCallClick = () => {
    onSelect('phone');
  };

  const handleEmailOption = (type: 'email-self' | 'email-draft') => {
    if (type === 'email-draft') {
      setSendStep('email-draft-inputs');
    } else {
      onSelect(type);
    }
  };

  const handleEmailDraftSubmit = () => {
    onSelect('email-draft', bulletPoints, useRecentCheckingNotes, useAccountNotes);
  };

  const resetForm = () => {
    setSendStep('select-send-method');
    setBulletPoints('');
    setUseRecentCheckingNotes(false);
    setUseAccountNotes(false);
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const modalContent = (
    <div style={{display: isOpen ? 'block' : 'none', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10001, pointerEvents: 'none'}}>
      <div style={{pointerEvents: 'auto'}}>
      {sendStep === 'select-send-method' ? (
        <>
          <div className={styles.optsWrapper}>
            <div
              style={{
                maxHeight: '300px',
                overflowY: 'auto'
              }}
            >
              <div className={styles.headerWrapper}>
                <p style={{fontSize: '16px', fontWeight: '700', textAlign: 'center'}}>How would you like to reach out?</p>
              </div>
              {store.currentPractitioner.email && (
                <button
                  className={styles.btn}
                  onClick={handleEmailClick}
                >
                  <span>EMAIL</span>
                  <span className={styles.smallText}>{store.currentPractitioner?.email}</span>
                </button>
              )}
              <button
                className={styles.btn}
                onClick={handleCallClick}
              >
                <span>CALL</span>
                <span className={styles.smallText}>{store.currentPractitioner?.phone}</span>
              </button>
            </div>
          </div>
          <div className={styles.cancelWrapper}>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'none',
                color: '#000',
                fontSize: '18px',
                borderRadius: '10px',
                border: 'none',
                textAlign: 'center',
                width: '100%'
              }}
            >
              CANCEL
            </button>
          </div>
        </>
      ) : sendStep === 'select-email-draft' ? (
        <>
          <div className={styles.optsWrapper}>
            <div
              style={{
                maxHeight: '300px',
                overflowY: 'auto'
              }}
            >
              <div className={styles.headerWrapper}>
                <p style={{fontSize: '16px', fontWeight: '700', textAlign: 'center'}}>Would you like me to draft an email for you?</p>
              </div>
              <button
                className={styles.btn}
                onClick={() => handleEmailOption('email-self')}
              >
                <span>NO</span>
                <span className={styles.smallText}>I'll do it myself</span>
              </button>
              <button
                className={styles.btn}
                onClick={() => handleEmailOption('email-draft')}
              >
                <span>YES</span>
                <span className={styles.smallText}>Please draft an email for this practitioner</span>
              </button>
            </div>
          </div>
          <div className={styles.cancelWrapper}>
            <button
              onClick={() => setSendStep('select-send-method')}
              style={{
                background: 'none',
                color: '#000',
                fontSize: '18px',
                borderRadius: '10px',
                border: 'none',
                textAlign: 'center',
                width: '100%'
              }}
            >
              BACK
            </button>
          </div>
        </>
      ) : (
        <>
          <div className={styles.optsWrapper}>
            <div
              style={{
                maxHeight: '350px',
                overflowY: 'auto'
              }}
            >
              <div className={styles.headerWrapper}>
                <p style={{fontSize: '16px', fontWeight: '700', textAlign: 'center', margin: 0}}>AI Email Drafting</p>
              </div>
              {draftResult ? (
                <>
                  <div className={styles.draftInputsWrapper}>
                    <div style={{marginTop: '12px', textAlign: 'center', color: '#1f1f1f'}}>
                      Your email draft is ready! Click the button below to open your email client with the drafted email.
                    </div>
                  </div>
                  <button
                    className={styles.btn}
                    onClick={openDraftLink}
                    style={{ padding: '15px' }}
                    disabled={isLoading}
                  >
                    OPEN EMAIL
                  </button>
                </>
              ) : (
                <>
                  <div className={styles.draftInputsWrapper}>
                    <label
                      style={{display: 'block', marginBottom: '8px'}}
                    >
                      Write a few bullet points on what should go into the email
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', margin: '0 12px' }}>
                      <div style={{
                        display: 'flex',
                        border: bulletPoints ? '1px solid #e0e0e0' : '2px dashed #d0d0d0',
                        borderRadius: '8px',
                        padding: '8px',
                        backgroundColor: bulletPoints ? '#fff' : '#fafafa',
                        transition: 'all 0.2s ease'
                      }}>
                        <textarea
                          style={{
                            flexGrow: 1,
                            height: '80px',
                            border: 0,
                            outline: 'none',
                            backgroundColor: 'transparent',
                            resize: 'none',
                            fontFamily: 'inherit',
                            fontSize: '15px',
                            lineHeight: '1.5'
                          }}
                          value={bulletPoints}
                          onChange={e => setBulletPoints(e.target.value)}
                        />
                      </div>
                      <div>
                        <div style={{marginBottom: '8px'}}>
                          <label>
                            <input
                              type="checkbox"
                              checked={useRecentCheckingNotes}
                              onChange={e => setUseRecentCheckingNotes(e.target.checked)}
                              style={{marginRight: '6px'}}
                            />
                            Use most recent check-in notes
                          </label>
                        </div>
                        <div>
                          <label>
                            <input
                              type="checkbox"
                              checked={useAccountNotes}
                              onChange={e => setUseAccountNotes(e.target.checked)}
                              style={{marginRight: '6px'}}
                            />
                            Use account notes
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className={styles.draftLoadingOverlay} style={{opacity: isLoading || error ? 1 : 0, zIndex: isLoading || error ? 10 : -1}}>
                      {isLoading ? (
                        <div style={{marginTop: '12px', textAlign: 'center', color: '#1f1f1f'}}>
                          We're drafting a few paragraphs for your email. Please wait...
                        </div>
                      ) : (
                        <div style={{marginTop: '12px', textAlign: 'center', color: '#1f1f1f'}}>
                          {error}
                        </div>
                      )}
                    </div>
                  </div>
                  {isLoading ? (
                    <button
                      className={styles.btn}
                      style={{ padding: '15px' }}
                      onClick={cancelDraftRequest}
                    >
                      CANCEL DRAFT
                    </button>
                  ) : (
                    <button
                      className={styles.btn}
                      onClick={handleEmailDraftSubmit}
                      style={{ padding: '15px' }}
                      disabled={isLoading}
                    >
                      WRITE DRAFT
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          <div className={styles.cancelWrapper}>
            <button
              onClick={() => setSendStep('select-email-draft')}
              style={{
                background: 'none',
                color: '#000',
                fontSize: '18px',
                borderRadius: '10px',
                border: 'none',
                textAlign: 'center',
                width: '100%'
              }}
            >
              BACK
            </button>
          </div>
        </>
      )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SendTab;
